import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Checkbox } from '@/components/ui/checkbox';
import { TurnstileWidget } from '@/components/auth/TurnstileWidget';
import { CuitLookup } from '@/components/auth/CuitLookup';
import { API_BASE } from '@/utils/api';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { QrCode, Mail, Lock, User, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Register() {
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [cuit, setCuit] = useState('');
  const [accept, setAccept] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [versions, setVersions] = useState(null);
  const onCaptcha = useCallback((t) => setCaptcha(t), []);
  useEffect(() => { axios.get(`${API_BASE}/public/config`).then(r => setVersions(r.data)).catch(() => {}); }, []);

  // Redirect if already authenticated
  if (isAuthenticated) {
    navigate('/dashboard', { replace: true });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      toast.error('La contraseña debe tener al menos 8 caracteres, con letras y números');
      return;
    }
    if (cuit.replace(/\D/g, '').length !== 11) {
      toast.error('Ingresá un CUIT de 11 dígitos');
      return;
    }
    if (!accept) {
      toast.error('Tenés que aceptar los términos y la política de privacidad');
      return;
    }
    if (!captcha) {
      toast.error('Completá la verificación anti-robots');
      return;
    }

    setLoading(true);
    try {
      await register(name, email, password, {
        cuit, accept_terms: accept, captcha_token: captcha,
        terms_version: versions?.terms_version, privacy_version: versions?.privacy_version,
      });
      toast.success('Cuenta creada. Revisá tu email para confirmarla.');
      navigate('/verificar-email', { replace: true });
    } catch (error) {
      toast.error(error.message);
      setCaptcha('');
      setCaptchaKey(k => k + 1);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
            <QrCode className="w-7 h-7 text-primary-foreground" />
          </div>
          <span className="font-heading text-2xl font-semibold">QR Mesa</span>
        </Link>

        <Card className="border-border shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-2xl">Crear Cuenta</CardTitle>
            <CardDescription>
              Comienza a gestionar tu negocio
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Tu nombre"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 h-12"
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12"
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-12"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cuit">CUIT</Label>
                <Input id="cuit" inputMode="numeric" placeholder="20-12345678-9" value={cuit} onChange={(e) => setCuit(e.target.value)} className="h-12" data-testid="cuit-input" />
                <CuitLookup cuit={cuit} />
                <p className="text-xs text-muted-foreground">La prueba gratuita es una por CUIT.</p>
              </div>
              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={accept} onCheckedChange={(v) => setAccept(!!v)} data-testid="accept-terms-checkbox" className="mt-0.5" />
                <span>Acepto los <a href="/terminos" target="_blank" rel="noreferrer" className="underline">términos y condiciones</a> y la <a href="/privacidad" target="_blank" rel="noreferrer" className="underline">política de privacidad</a>.</span>
              </label>
              <TurnstileWidget key={captchaKey} onToken={onCaptcha} />
              <Button 
                type="submit" 
                className="w-full h-12 rounded-full"
                disabled={loading || !accept || !captcha}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Creando cuenta...
                  </>
                ) : (
                  'Crear Cuenta'
                )}
              </Button>
            </form>
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground text-center">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="text-primary hover:underline font-medium">
                Inicia sesión
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
